import { useCallback, useEffect } from "react";
import { UseFormReturn, FieldValues } from "react-hook-form";
import { useFormManager } from "./use-form-manager";

interface UseFormModalOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  draftKey: string;
  isOpen: boolean;
  onClose: () => void;
  autoSaveDraft?: boolean;
  clearDraftOnClose?: boolean;
  clearDraftOnSuccess?: boolean;
}

export function useFormModal<T extends FieldValues>(options: UseFormModalOptions<T>) {
  const {
    form,
    draftKey,
    isOpen,
    onClose,
    autoSaveDraft = true,
    clearDraftOnClose = true,
    clearDraftOnSuccess = true,
  } = options;

  // Initialize form manager with draft support
  const formManager = useFormManager({
    form,
    draftKey,
    isOpen,
    autoSaveDraft,
  });

  // Auto-save form changes to draft
  useEffect(() => {
    if (isOpen && autoSaveDraft) {
      const subscription = form.watch((values) => {
        formManager.saveDraft(values as T);
      });
      return () => subscription.unsubscribe();
    }
  }, [isOpen, form, formManager.saveDraft, formManager, autoSaveDraft]);

  // Handle modal close with draft management
  const handleClose = useCallback(() => {
    if (clearDraftOnClose) {
      formManager.clearDraft();
    }
    onClose();
  }, [formManager, onClose, clearDraftOnClose]);

  // Enhanced mutation handlers with draft clearing
  const createMutationHandlers = useCallback(
    (
      customOptions: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
        successMessage?: string;
        clearDraftOnSuccess?: boolean;
      } = {}
    ) => {
      const {
        onSuccess,
        onError,
        successMessage,
        clearDraftOnSuccess: customClearDraftOnSuccess = clearDraftOnSuccess,
      } = customOptions;

      return formManager.createMutationHandlers({
        onSuccess: () => {
          if (customClearDraftOnSuccess) {
            formManager.clearDraft();
          }
          onSuccess?.();
        },
        onError,
        successMessage,
      });
    },
    [formManager, clearDraftOnSuccess]
  );

  return {
    formManager,
    handleClose,
    createMutationHandlers,
    hasDraft: formManager.draft && Object.keys(formManager.draft).length > 0,
  };
}
